import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./components/ui/table";
import { Button } from "./components/ui/button";
import {
  ArrowLeft, Package, AlertTriangle, TrendingUp, ChevronLeft, ChevronRight,
  Filter, X, Search, ClipboardList, Database, Download, Clock, CornerDownLeft,
} from "lucide-react";
import StockReport from "./StockReport";
import StockLedger from "./StockLedger";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDateIST = (s) => {
  if (!s) return "-";
  try {
    const d = new Date(s.includes("T") ? s : s + "T00:00:00Z");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch { return "-"; }
};

const isDateExpired = (dateStr) => {
  if (!dateStr) return false;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    return d < new Date();
  } catch { return false; }
};

const formatCurrency = (v) => `\u20B9${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TABS = [
  { id: "zero", label: "Zero Stock", icon: Package, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  { id: "minimum", label: "Minimum Stock", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  { id: "expiry", label: "Expired / Near Expiry", icon: Clock, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200" },
  { id: "fast", label: "Fast Moving", icon: TrendingUp, color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-200" },
  { id: "itemstock", label: "Item wise Stock Report", icon: Database, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
  { id: "stock", label: "Stock Analytics", icon: ClipboardList, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
  { id: "ledger", label: "Stock Ledger", icon: CornerDownLeft, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
];

export default function InventoryReport() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("zero");
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({ category: "", sub_category: "", item_name: "", batch_number: "" });
  const [filterOptions, setFilterOptions] = useState({ categories: [], sub_categories: [] });
  const [stockFilter, setStockFilter] = useState("low");

  // Item Stock Data tab state
  const [itemStockData, setItemStockData] = useState([]);
  const [itemStockTotal, setItemStockTotal] = useState(0);
  const [itemStockPage, setItemStockPage] = useState(1);
  const [itemStockTotalPages, setItemStockTotalPages] = useState(0);
  const [itemStockLoading, setItemStockLoading] = useState(false);
  const [itemStockLocations, setItemStockLocations] = useState([]);
  const [itemStockFilters, setItemStockFilters] = useState({ item_name: "", batch_number: "", location: "", category: "", sub_category: "" });
  const [exportingCSV, setExportingCSV] = useState(false);

  // Expiry tab state
  const [expiryData, setExpiryData] = useState([]);
  const [expiryTotal, setExpiryTotal] = useState(0);
  const [expiryPage, setExpiryPage] = useState(1);
  const [expiryTotalPages, setExpiryTotalPages] = useState(0);
  const [expiryLoading, setExpiryLoading] = useState(false);
  const [expiryType, setExpiryType] = useState("expired"); // "expired", "near", "all"

  // Batch vs Item view toggle (applies to zero, minimum, fast, expiry tabs)
  const [batchWise, setBatchWise] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/inventory/filters`).then(r => setFilterOptions(r.data)).catch(() => {});
    axios.get(`${API}/api/inventory/summary`).then(r => setSummary(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (tab === "itemstock" || tab === "stock" || tab === "expiry" || tab === "ledger") return; // handled separately
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

      let endpoint = "";
      if (tab === "zero") endpoint = "/api/inventory/zero-stock";
      else if (tab === "minimum") endpoint = "/api/inventory/minimum-stock";
      else {
        endpoint = "/api/inventory/fast-moving";
        params.stock_filter = stockFilter;
      }
      if (!batchWise) params.group_by = "item";

      const { data: res } = await axios.get(`${API}${endpoint}`, { params });
      setData(res.data);
      setTotal(res.total);
      setTotalPages(res.total_pages);
    } catch (e) {
      toast.error("Failed to load inventory data");
    }
    setLoading(false);
  }, [tab, page, filters, stockFilter, batchWise]);

  // Fetch Item Stock Data separately
  const fetchItemStock = useCallback(async () => {
    if (tab !== "itemstock") return;
    setItemStockLoading(true);
    try {
      const params = { page: itemStockPage, page_size: 50 };
      if (itemStockFilters.item_name) params.item_name = itemStockFilters.item_name;
      if (itemStockFilters.batch_number) params.batch_number = itemStockFilters.batch_number;
      if (itemStockFilters.location) params.location = itemStockFilters.location;
      if (itemStockFilters.category) params.category = itemStockFilters.category;
      if (itemStockFilters.sub_category) params.sub_category = itemStockFilters.sub_category;

      const { data: res } = await axios.get(`${API}/api/inventory/item-stock`, { params });
      setItemStockData(res.data);
      setItemStockTotal(res.total);
      setItemStockTotalPages(res.total_pages);
      if (res.locations) setItemStockLocations(res.locations);
    } catch (e) {
      toast.error("Failed to load item stock data");
    }
    setItemStockLoading(false);
  }, [tab, itemStockPage, itemStockFilters]);

  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      const params = {};
      if (itemStockFilters.item_name) params.item_name = itemStockFilters.item_name;
      if (itemStockFilters.batch_number) params.batch_number = itemStockFilters.batch_number;
      if (itemStockFilters.location) params.location = itemStockFilters.location;
      if (itemStockFilters.category) params.category = itemStockFilters.category;
      if (itemStockFilters.sub_category) params.sub_category = itemStockFilters.sub_category;

      const res = await axios.get(`${API}/api/inventory/item-stock/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "item_stock.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (e) {
      toast.error("Failed to export CSV");
    }
    setExportingCSV(false);
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchItemStock(); }, [fetchItemStock]);
  useEffect(() => { setPage(1); }, [tab, filters, stockFilter, batchWise]);
  useEffect(() => { setItemStockPage(1); }, [itemStockFilters]);

  // Expiry stock fetch
  const fetchExpiryData = useCallback(async () => {
    if (tab !== "expiry") return;
    setExpiryLoading(true);
    try {
      const params = { page: expiryPage, page_size: 50, expiry_type: expiryType };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (!batchWise) params.group_by = "item";
      const { data: res } = await axios.get(`${API}/api/inventory/expiry-stock`, { params });
      setExpiryData(res.data);
      setExpiryTotal(res.total);
      setExpiryTotalPages(res.total_pages);
    } catch (e) { toast.error("Failed to load expiry stock data"); }
    setExpiryLoading(false);
  }, [tab, expiryPage, expiryType, filters, batchWise]);

  useEffect(() => { fetchExpiryData(); }, [fetchExpiryData]);
  useEffect(() => { setExpiryPage(1); }, [expiryType]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const clearFilters = () => setFilters({ category: "", sub_category: "", item_name: "", batch_number: "" });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-base font-bold text-slate-800 tracking-tight">Inventory Report</h1>
            <p className="text-[11px] text-slate-400">{summary.total_items || 0} total items</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Batch vs Item toggle — shown for zero, minimum, fast, expiry tabs */}
          {["zero", "minimum", "fast", "expiry"].includes(tab) && (
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="batch-item-toggle">
              <button onClick={() => setBatchWise(true)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${batchWise ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="toggle-batch">Batch Wise</button>
              <button onClick={() => setBatchWise(false)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${!batchWise ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="toggle-item">Item Wise</button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
            className={activeFilterCount > 0 ? "border-teal-500 text-teal-700" : ""} data-testid="toggle-filters">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
        </div>
      </div>

      {/* Filters Panel (only for zero/minimum/fast tabs) */}
      {showFilters && !["stock", "itemstock"].includes(tab) && (
        <div className="bg-white border-b border-slate-200 px-6 py-4" data-testid="filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Category</label>
              <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10" data-testid="filter-category">
                <option value="">All Categories</option>
                {filterOptions.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Sub Category</label>
              <select value={filters.sub_category} onChange={e => setFilters(f => ({ ...f, sub_category: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10" data-testid="filter-subcategory">
                <option value="">All Sub Categories</option>
                {filterOptions.sub_categories.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Item Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <input value={filters.item_name} onChange={e => setFilters(f => ({ ...f, item_name: e.target.value }))}
                  placeholder="Search item..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10" data-testid="filter-item-name" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Batch Number</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <input value={filters.batch_number} onChange={e => setFilters(f => ({ ...f, batch_number: e.target.value }))}
                  placeholder="Search batch..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-slate-900/10" data-testid="filter-batch" />
              </div>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <div className="mt-3 flex justify-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500">
                <X className="w-3 h-3 mr-1" /> Clear Filters
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="px-6 py-4 space-y-4">
        {/* Tab Cards */}
        <div className="grid grid-cols-6 gap-3">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`rounded-xl p-3 border-2 text-left transition-all ${tab === t.id ? `${t.bg} ${t.border} shadow-sm` : "bg-white border-slate-200 hover:border-slate-300"}`}
              data-testid={`tab-${t.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <t.icon className={`w-4 h-4 ${tab === t.id ? t.color : "text-slate-400"}`} />
                <span className={`text-xs font-semibold uppercase tracking-wider ${tab === t.id ? t.color : "text-slate-400"}`}>{t.label}</span>
              </div>
              <p className={`text-2xl font-bold ${tab === t.id ? t.color : "text-slate-700"}`}>
                {t.id === "zero" ? summary.zero_stock || 0 : t.id === "minimum" ? summary.minimum_stock || 0 : t.id === "expiry" ? ((summary.expired || 0) + (summary.near_expiry || 0)) : t.id === "itemstock" ? (tab === "itemstock" ? itemStockTotal : "") : t.id === "stock" ? "" : "-"}
              </p>
              {t.id === "stock" && <p className="text-[10px] text-slate-400 mt-0.5">Purchase & Movement Analysis</p>}
              {t.id === "itemstock" && <p className="text-[10px] text-slate-400 mt-0.5">All Items by Name</p>}
              {t.id === "expiry" && <p className="text-[10px] text-slate-400 mt-0.5">{summary.expired || 0} expired + {summary.near_expiry || 0} near expiry</p>}
            </button>
          ))}
        </div>

        {/* Stock Report (separate component with its own state) */}
        {tab === "stock" ? (
          <StockReport />
        ) : tab === "ledger" ? (
          <StockLedger />
        ) : tab === "itemstock" ? (
          <ItemStockTable
            data={itemStockData}
            total={itemStockTotal}
            page={itemStockPage}
            totalPages={itemStockTotalPages}
            loading={itemStockLoading}
            setPage={setItemStockPage}
            filters={itemStockFilters}
            setFilters={setItemStockFilters}
            locations={itemStockLocations}
            filterOptions={filterOptions}
            showFilters={showFilters}
            onExportCSV={handleExportCSV}
            exportingCSV={exportingCSV}
          />
        ) : tab === "expiry" ? (
          <ExpiryStockTable
            data={expiryData}
            total={expiryTotal}
            page={expiryPage}
            totalPages={expiryTotalPages}
            loading={expiryLoading}
            setPage={setExpiryPage}
            expiryType={expiryType}
            setExpiryType={setExpiryType}
            batchWise={batchWise}
            onRefresh={fetchExpiryData}
          />
        ) : (
        <>
        {/* Fast Moving Stock Filter */}
        {tab === "fast" && (
          <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-4 py-2">
            <span className="text-xs font-semibold text-slate-500 mr-2">Stock Filter:</span>
            {[{ id: "low", label: "No Stock & Minimal" }, { id: "all", label: "All Items" }].map(sf => (
              <button key={sf.id} onClick={() => setStockFilter(sf.id)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${stockFilter === sf.id ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`stock-filter-${sf.id}`}>{sf.label}</button>
            ))}
          </div>
        )}

        {/* Results Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">{total} items | Page {page} of {totalPages || 1}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Category</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Sub Category</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Code</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Name</TableHead>
                  {batchWise
                    ? <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Batch No</TableHead>
                    : <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Batches</TableHead>}
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Stock Qty</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Location</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">UOM</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Pack Size</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">MRP</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">Unit Price</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">Discount</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Mfg Date</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Expiry</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Last Billed</TableHead>
                  {tab === "fast" && <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Bill Count</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={tab === "fast" ? 16 : 15} className="text-center py-12 text-sm text-slate-400">Loading...</TableCell></TableRow>
                )}
                {!loading && data.length === 0 && (
                  <TableRow><TableCell colSpan={tab === "fast" ? 16 : 15} className="text-center py-12 text-sm text-slate-400">No items found</TableCell></TableRow>
                )}
                {!loading && data.map((item, i) => {
                  const isExpired = isDateExpired(item.expiryDate);
                  return (
                    <TableRow key={`${item.itemCode}-${item.batchNumber}-${i}`} className="hover:bg-slate-50 transition-colors" data-testid={`inv-row-${i}`}>
                      <TableCell className="text-xs max-w-[100px] truncate">{item.category || "-"}</TableCell>
                      <TableCell className="text-xs max-w-[90px] truncate">{item.subCategory || "-"}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-600">{item.itemCode || "-"}</TableCell>
                      <TableCell className="text-xs font-medium text-slate-800 max-w-[180px] truncate">{item.itemName || "-"}</TableCell>
                      {batchWise
                        ? <TableCell className="text-xs font-mono">{item.batchNumber || "-"}</TableCell>
                        : <TableCell className="text-xs text-center font-medium text-slate-600">{item.batchCount || "-"}</TableCell>}
                      <TableCell className="text-xs text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${item.currentStock === 0 ? "bg-red-100 text-red-700" : item.currentStock <= (item.minimumStockLevel || 0) ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {item.currentStock}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs max-w-[80px] truncate">{item.department || item.location || "-"}</TableCell>
                      <TableCell className="text-xs">{item.unitOfMeasure || "-"}</TableCell>
                      <TableCell className="text-xs text-center">{item.packSize || "-"}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-semibold">{item.mrp ? formatCurrency(item.mrp) : "-"}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{item.unitPrice ? formatCurrency(item.unitPrice) : "-"}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{item.discount ? `${item.discount}%` : "-"}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{item.manufacturingDate || "-"}</TableCell>
                      <TableCell className={`text-xs whitespace-nowrap ${isExpired ? "text-red-600 font-semibold" : ""}`}>{item.expiryDate || "-"}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{item.lastPurchased || "-"}</TableCell>
                      {tab === "fast" && <TableCell className="text-xs text-center font-bold text-teal-700">{item.billCount || 0}</TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs font-semibold text-slate-600 px-2">{page} / {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}


function ItemStockTable({ data, total, page, totalPages, loading, setPage, filters, setFilters, locations, filterOptions, showFilters, onExportCSV, exportingCSV }) {
  return (
    <div className="space-y-4">
      {/* Inline Filters for Item Stock */}
      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid="item-stock-filters">
          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Item Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <input value={filters.item_name} onChange={e => setFilters(f => ({ ...f, item_name: e.target.value }))}
                  placeholder="Search item..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/10" data-testid="item-stock-filter-name" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Batch</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <input value={filters.batch_number} onChange={e => setFilters(f => ({ ...f, batch_number: e.target.value }))}
                  placeholder="Search batch..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/10" data-testid="item-stock-filter-batch" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Location</label>
              <select value={filters.location} onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/10" data-testid="item-stock-filter-location">
                <option value="">All Locations</option>
                {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Category</label>
              <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/10" data-testid="item-stock-filter-category">
                <option value="">All Categories</option>
                {filterOptions.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">Sub Category</label>
              <select value={filters.sub_category} onChange={e => setFilters(f => ({ ...f, sub_category: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/10" data-testid="item-stock-filter-subcategory">
                <option value="">All Sub Categories</option>
                {filterOptions.sub_categories.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {Object.values(filters).some(Boolean) && (
            <div className="mt-3 flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setFilters({ item_name: "", batch_number: "", location: "", category: "", sub_category: "" })} className="text-xs text-slate-500">
                <X className="w-3 h-3 mr-1" /> Clear Filters
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500">{total} items | Page {page} of {totalPages || 1}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onExportCSV} disabled={exportingCSV} className="text-xs" data-testid="item-stock-export-csv">
              <Download className="w-3.5 h-3.5 mr-1.5" /> {exportingCSV ? "Exporting..." : "Export CSV"}
            </Button>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="item-stock-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="item-stock-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Name</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Code</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">HSN Code</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Composition</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Pack Info</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Location</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Batch</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Stock Qty</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">UoM</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Pack Size</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">Unit Price</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">MRP</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Mfg Date</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Expiry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={12} className="text-center py-12 text-sm text-slate-400">Loading...</TableCell></TableRow>
              )}
              {!loading && data.length === 0 && (
                <TableRow><TableCell colSpan={12} className="text-center py-12 text-sm text-slate-400">No items found</TableCell></TableRow>
              )}
              {!loading && data.map((item, i) => {
                const isExpired = isDateExpired(item.expiryDate);
                const rowText = isExpired ? "text-red-600" : "";
                return (
                <TableRow key={`${item.itemCode}-${item.batchNumber}-${i}`} className={`hover:bg-slate-50 transition-colors ${isExpired ? "bg-red-50/50" : ""}`} data-testid={`item-stock-row-${i}`}>
                  <TableCell className={`text-xs font-medium max-w-[200px] truncate ${rowText || "text-slate-800"}`}>{item.itemName || "-"}</TableCell>
                  <TableCell className={`text-xs font-mono ${rowText || "text-slate-600"}`}>{item.itemCode || "-"}</TableCell>
                  <TableCell className={`text-xs font-mono ${rowText || "text-slate-500"}`}>{item.hsnCode || "-"}</TableCell>
                  <TableCell className={`text-xs max-w-[180px] truncate ${rowText || "text-slate-600"}`} title={item.composition || ""}>{item.composition || "-"}</TableCell>
                  <TableCell className={`text-xs max-w-[140px] truncate ${rowText || "text-slate-600"}`} title={item.packInfo || ""}>{item.packInfo || "-"}</TableCell>
                  <TableCell className={`text-xs max-w-[100px] truncate ${rowText}`}>{item.location || "-"}</TableCell>
                  <TableCell className={`text-xs font-mono ${rowText}`}>{item.batchNumber || "-"}</TableCell>
                  <TableCell className="text-xs text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${item.currentStock === 0 ? "bg-red-100 text-red-700" : isExpired ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {item.currentStock}
                    </span>
                  </TableCell>
                  <TableCell className={`text-xs ${rowText}`}>{item.unitOfMeasure || "-"}</TableCell>
                  <TableCell className={`text-xs text-center ${rowText}`}>{item.packSize || "-"}</TableCell>
                  <TableCell className={`text-xs text-right tabular-nums ${rowText}`}>{item.unitPrice ? `\u20B9${item.unitPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</TableCell>
                  <TableCell className={`text-xs text-right tabular-nums font-semibold ${rowText}`}>{item.mrp ? `\u20B9${item.mrp.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</TableCell>
                  <TableCell className={`text-xs whitespace-nowrap ${rowText}`}>{item.manufacturingDate || "-"}</TableCell>
                  <TableCell className={`text-xs whitespace-nowrap ${isExpired ? "text-red-600 font-semibold" : ""}`}>{item.expiryDate || "-"}</TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs font-semibold text-slate-600 px-2">{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



function ExpiryStockTable({ data, total, page, totalPages, loading, setPage, expiryType, setExpiryType, batchWise, onRefresh }) {
  const [returnModal, setReturnModal] = useState(null);
  const [returnedBy, setReturnedBy] = useState("");
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [returnedLoading, setReturnedLoading] = useState(false);
  const [returnedPage, setReturnedPage] = useState(1);
  const [returnedTotal, setReturnedTotal] = useState(0);
  const [returnedTotalPages, setReturnedTotalPages] = useState(0);

  const fetchReturned = useCallback(async () => {
    if (expiryType !== "returned") return;
    setReturnedLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/api/inventory/expiry-returns`, { params: { page: returnedPage, page_size: 50 } });
      setReturnedData(res.data || []);
      setReturnedTotal(res.total || 0);
      setReturnedTotalPages(res.total_pages || 0);
    } catch (e) { toast.error("Failed to load returned items"); }
    finally { setReturnedLoading(false); }
  }, [expiryType, returnedPage]);

  useEffect(() => { fetchReturned(); }, [fetchReturned]);

  const handleReturn = async () => {
    if (!returnedBy.trim()) { toast.error("Please enter the name of the person returning"); return; }
    setReturnLoading(true);
    try {
      await axios.post(`${API}/api/inventory/expiry-return`, {
        inventoryId: returnModal.inventoryId,
        itemName: returnModal.itemName,
        itemCode: returnModal.itemCode,
        batchNumber: returnModal.batchNumber,
        returnedBy: returnedBy.trim(),
        quantity: returnModal.currentStock,
      });
      toast.success(`${returnModal.itemName} marked as returned to manufacturer`);
      setReturnModal(null);
      setReturnedBy("");
      if (onRefresh) onRefresh();
    } catch (e) { toast.error("Failed to mark return"); }
    finally { setReturnLoading(false); }
  };

  return (
    <div className="space-y-4">
      {/* Type toggle */}
      <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-4 py-2">
        <span className="text-xs font-semibold text-slate-500 mr-2">Show:</span>
        {[
          { id: "expired", label: "Expired", color: "bg-red-600" },
          { id: "near", label: "Near Expiry (30 days)", color: "bg-amber-500" },
          { id: "all", label: "All", color: "bg-rose-600" },
          { id: "returned", label: "Returned to Mfg", color: "bg-indigo-600" },
        ].map(opt => (
          <button key={opt.id} onClick={() => setExpiryType(opt.id)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${expiryType === opt.id ? `${opt.color} text-white` : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`expiry-filter-${opt.id}`}>{opt.label}</button>
        ))}
      </div>

      {/* Returned Items View */}
      {expiryType === "returned" ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">{returnedTotal} returned items | Page {returnedPage} of {returnedTotalPages || 1}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={returnedPage <= 1} onClick={() => setReturnedPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" disabled={returnedPage >= returnedTotalPages} onClick={() => setReturnedPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-indigo-50/60">
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Name</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Code</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Batch</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Qty</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Returned By</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Return Date & Time (IST)</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returnedLoading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">Loading...</TableCell></TableRow>
                )}
                {!returnedLoading && returnedData.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">No returned items found</TableCell></TableRow>
                )}
                {!returnedLoading && returnedData.map((item, i) => (
                  <TableRow key={item.id} className="hover:bg-slate-50" data-testid={`returned-row-${i}`}>
                    <TableCell className="text-xs font-medium text-slate-800">{item.itemName}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-600">{item.itemCode}</TableCell>
                    <TableCell className="text-xs font-mono">{item.batchNumber}</TableCell>
                    <TableCell className="text-xs text-center font-medium">{item.quantity || "-"}</TableCell>
                    <TableCell className="text-xs font-semibold text-indigo-700">{item.returnedBy}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{item.returnDateIST}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{item.notes || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
      <>
      {/* Expired / Near Expiry Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500">{total} items | Page {page} of {totalPages || 1}</span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="expiry-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="expiry-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Status</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Name</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Item Code</TableHead>
                {batchWise
                  ? <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Batch</TableHead>
                  : <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Batches</TableHead>}
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Category</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Department</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Stock Qty</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">UoM</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-right">MRP</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Mfg Date</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap">Expiry Date</TableHead>
                <TableHead className="text-[10px] font-bold uppercase whitespace-nowrap text-center">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={12} className="text-center py-12 text-sm text-slate-400">Loading...</TableCell></TableRow>
              )}
              {!loading && data.length === 0 && (
                <TableRow><TableCell colSpan={12} className="text-center py-12 text-sm text-slate-400">No items found</TableCell></TableRow>
              )}
              {!loading && data.map((item, i) => {
                const rowClass = item.isExpired ? "bg-red-50/60" : "bg-amber-50/40";
                const textClass = item.isExpired ? "text-red-600" : "text-amber-700";
                return (
                  <TableRow key={`${item.itemCode}-${item.batchNumber}-${i}`} className={`${rowClass} hover:bg-slate-50 transition-colors`} data-testid={`expiry-row-${i}`}>
                    <TableCell className="text-xs">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${item.isExpired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {item.expiryStatus}
                      </span>
                    </TableCell>
                    <TableCell className={`text-xs font-medium max-w-[200px] truncate ${textClass}`}>{item.itemName || "-"}</TableCell>
                    <TableCell className={`text-xs font-mono ${textClass}`}>{item.itemCode || "-"}</TableCell>
                    {batchWise
                      ? <TableCell className={`text-xs font-mono ${textClass}`}>{item.batchNumber || "-"}</TableCell>
                      : <TableCell className="text-xs text-center font-medium text-slate-600">{item.batchCount || "-"}</TableCell>}
                    <TableCell className="text-xs text-slate-500 max-w-[100px] truncate">{item.category || "-"}</TableCell>
                    <TableCell className="text-xs">{item.department || "-"}</TableCell>
                    <TableCell className="text-xs text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">{item.currentStock}</span>
                    </TableCell>
                    <TableCell className="text-xs">{item.unitOfMeasure || "-"}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold">{item.mrp ? `\u20B9${item.mrp.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{item.manufacturingDate || "-"}</TableCell>
                    <TableCell className={`text-xs whitespace-nowrap font-semibold ${textClass}`}>{item.expiryDate || "-"}</TableCell>
                    <TableCell className="text-center">
                      {item.isExpired && batchWise && (
                        <button onClick={() => setReturnModal(item)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                          data-testid={`return-btn-${i}`}>
                          <CornerDownLeft className="w-3 h-3" /> Return
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-xs font-semibold text-slate-600 px-2">{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* Return Modal */}
      {returnModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReturnModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="return-modal">
            <h3 className="text-base font-bold text-slate-800 mb-1">Return to Manufacturer</h3>
            <p className="text-xs text-slate-400 mb-4">Mark this expired product as returned</p>
            <div className="space-y-3">
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-xs font-semibold text-red-800">{returnModal.itemName}</p>
                <p className="text-[10px] text-red-600 mt-0.5">Code: {returnModal.itemCode} | Batch: {returnModal.batchNumber} | Qty: {returnModal.currentStock}</p>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Returned By *</label>
                <input value={returnedBy} onChange={e => setReturnedBy(e.target.value)} placeholder="Name of person returning"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-300"
                  data-testid="return-by-input" autoFocus />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => { setReturnModal(null); setReturnedBy(""); }} className="flex-1">Cancel</Button>
                <Button size="sm" onClick={handleReturn} disabled={returnLoading || !returnedBy.trim()}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-return-btn">
                  {returnLoading ? "Saving..." : "Confirm Return"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
